import { useEffect, useRef, useState } from "react";
import { OverlayGrayBackground } from "./OverlayGrayBackground";
import { useDispatch, useSelector } from "react-redux";
import { addProductByVendor, cleanUpSearchProductsForVendor, cleanUpVendorProducts, deleteProductByVendor, getProductsForVendorBySearch, getVendorProducts, VendorsLoadingKeys } from "../slices/managementSlices/vendorsSlice";
import Drawer from "./Drawer";
import { ArrowPathIcon, MagnifyingGlassIcon, TrashIcon } from "@heroicons/react/24/outline";
import { TanStackTable } from "./TanStackTable";
import { enumInventoryStatuses } from "../consts/inventoryStatus";
import { arrayHasItems, getInventoryStatusColor } from "../helpers/common";
import { ConfrimModal } from "./ConfrimModal";
import { useDebounce } from "../helpers/useDebounce";
import { getStatusColor } from "./StatusSelect";
import { Pagination } from "./Pagination";
import { LoaderSpinner } from "./LoaderSpinner";
import { VendorProductSearchSelect } from "./VendorProductSearchSelect";

export const VendorProductsSidebar = ({
  vendorId,
  onClose,
  isOpen
}) => {
    const { 
        loadingKeys, 
        loading, 
        vendorProductsData, 
        searchProductsByVendor,
        firstLoadCompleteForProducts
    } = useSelector((state) => state.vendors);
    const headerDrawerRef = useRef(null);
    const [headerDrawerHeight, setHeaderDrawerHeight] = useState(0);
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [productIdToDelete, setProductIdToDelete] = useState("");
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const currentSearchValue = useDebounce(searchTerm, 300);

    const [productsCurrentPage, setProductsCurrentPage] = useState(1);
    const [productsCurrentItemsPerPage, setProductsCurrentItemsPerPage] = useState(10);

    const [productQuery, setProductQuery] = useState("");
    const currentNewProductSearch = useDebounce(productQuery, 300);
    useEffect(() => {
        if (currentNewProductSearch) {
            dispatch(getProductsForVendorBySearch({ search: currentNewProductSearch, vendorId }));
        }
    }, [currentNewProductSearch]);
    
    useEffect(() => {
        if (!isOpen) {
            dispatch(cleanUpVendorProducts({}));
            dispatch(cleanUpSearchProductsForVendor({}));
            setSearchTerm("");
            setProductQuery("");
        }
    }, [isOpen])

    useEffect(() => {
        if (headerDrawerRef?.current) {
            setHeaderDrawerHeight(headerDrawerRef?.current?.getBoundingClientRect()?.height);
        }
    }, [headerDrawerRef?.current?.getBoundingClientRect()?.height]);

    useEffect(() => {
        if (vendorId && isOpen) {
            dispatch(getVendorProducts({ 
                id: vendorId,
                search: currentSearchValue || "",
                page: productsCurrentPage,
                itemsPerPage: productsCurrentItemsPerPage
            }));
        }
    }, [
        vendorId,
        currentSearchValue,
        productsCurrentPage,
        productsCurrentItemsPerPage
    ]);

    const getVendorProductsIsLoading = () => 
        (loading && loadingKeys.includes(VendorsLoadingKeys.vendorProducts)) ||
        (loading && loadingKeys.includes(VendorsLoadingKeys.deleteProductByVendor)) ||
        (loading && loadingKeys.includes(VendorsLoadingKeys.addProductToTheVendor));
    const isDeleteProductLoading = () => loadingKeys.includes(VendorsLoadingKeys.deleteProductByVendor);

    const title = vendorProductsData?.name ? `Products by ${vendorProductsData?.name || ""}` : "Products";

    return (
        <Drawer isOpen={isOpen} onClose={onClose} title={title} size={"xl"} headerDrawerRef={headerDrawerRef}>
            <div className="p-6 flex flex-col gap-4">
                <div className="p-3 bg-gray-200 rounded-lg">
                    <VendorProductSearchSelect
                        minCharsToSearch={3}
                        options={searchProductsByVendor}
                        searchPlaceholder="Search for a product to add"
                        onSelect={async (option) => {
                            const id = option?.value || option?.id || null;
                            if (!id) return;
                            await dispatch(addProductByVendor({
                                vendorId,
                                productId: id, 
                                search: currentSearchValue,
                                searchProductToAdd: currentNewProductSearch,
                                page: productsCurrentPage,
                                itemsPerPage: productsCurrentItemsPerPage
                            }))
                        }} 
                        onSearch={setProductQuery}
                        renderOption={(option) => (
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Name: {option.name}</p>
                                    <p className="text-sm text-gray-500">SKU: {option.sku}</p>
                                </div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(option.status)}`}>
                                    {enumInventoryStatuses[option.status]}
                                </span>
                            </div>
                        )}
                    />
                </div>
                <div className="p-3 bg-gray-200 rounded-lg relative overflow-hidden">
                    {getVendorProductsIsLoading() && <OverlayGrayBackground />}
                    <div>
                        <div className="relative w-72 mb-2">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 py-2 px-4 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            {searchTerm && (
                                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                        aria-label="Clear search"
                                    >
                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                        {!firstLoadCompleteForProducts ? (
                            <LoaderSpinner height="40" width="40" border="3" colorSpinner="black" />
                        ) : (
                            <div>
                                {arrayHasItems(vendorProductsData?.products) ? (
                                    <TanStackTable
                                        key={`${vendorProductsData?.products}_${headerDrawerHeight}`}
                                        overrideStickyOptions={{
                                            top: headerDrawerHeight,
                                            left: 36
                                        }}
                                        data={vendorProductsData?.products || []}
                                        hasActions={true}
                                        enableSorting={false}
                                        columns={[
                                            { 
                                                accessorKey: "name", 
                                                header: "Product Name", 
                                                enableSorting: false,
                                                cell: ({ row }) => {
                                                    return (
                                                        <div className="flex flex-col gap-1 max-w-[400px]">
                                                            <span className="block whitespace-break-spaces">{row.original.name}</span>
                                                            <span>SKU: {row.original.sku}</span>
                                                        </div>
                                                    )
                                                }
                                            },
                                            {
                                                accessorKey: "status",
                                                header: "Status",
                                                enableSorting: false,
                                                cell: ({ row }) => {
                                                    const value = row.original.status;
                                                    return (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getInventoryStatusColor(value)}`}>
                                                            {value ? enumInventoryStatuses[value] : null}
                                                        </span>
                                                    );
                                                }
                                            },
                                            {
                                                accessorKey: "actions", header: "Actions", enableSorting: false, cell: ({ row }) => {
                                                return (
                                                <div className={`flex gap-2 ${row.index % 2 === 0 ? '!bg-white' : '!bg-gray-50'}`}>
                                                    <button
                                                        className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setProductIdToDelete(row.original.id);
                                                            setDeleteModalOpened(true);
                                                        }}
                                                    >
                                                        {isDeleteProductLoading() && productIdToDelete === row.original.id ? <ArrowPathIcon className="animate-spin h-4 w-4" /> : <TrashIcon className="h-4 w-4" aria-hidden="true" />}
                                                    </button>
                                                </div>
                                                )
                                            }
                                            },
                                        ]}
                                    />
                                ) : (
                                    "No products"
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div>
                    <Pagination
                        key={vendorProductsData?.pagination?.rangeRows}
                        currentItemsPerPage={productsCurrentItemsPerPage}
                        rangeRows={vendorProductsData?.pagination?.rangeRows}
                        currentPage={productsCurrentPage}
                        setCurrentPage={setProductsCurrentPage}
                        setCurrentItemsPerPage={setProductsCurrentItemsPerPage}
                        isTheLastPage={vendorProductsData?.pagination?.isTheLastPage}
                        totalPages={vendorProductsData?.pagination?.totalPages}
                    />
                </div>
            </div>
            <ConfrimModal
                isOpen={deleteModalOpened}
                title={"Are you sure want to delete this product from vendor?"}
                confirmButtonTitle={"Delete"}
                containerClassName={"max-w-[80%]"}
                isLoading={isDeleteProductLoading()}
                submitClick={async () => {
                    setDeleteModalOpened(false)
                    await dispatch(deleteProductByVendor({ 
                        vendorId,
                        productId: productIdToDelete, 
                        search: currentSearchValue,
                        page: productsCurrentPage,
                        itemsPerPage: productsCurrentItemsPerPage
                    }))
                    setProductIdToDelete("")
                }}
                cancelClick={() => {
                    setDeleteModalOpened(false)
                    setProductIdToDelete("")
                }}
            />
        </Drawer>
    );
};